import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CalendarDays, MapPin, ChevronRight } from 'lucide-react';
import type { Rental } from '@tools-beaver/shared';
import { RENTAL_STATUS_LABELS } from '@tools-beaver/shared';
import { apiClient } from '../api/client';
import { useAppStore } from '../store/useAppStore';

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  ACCEPTED: 'bg-blue-100 text-blue-700',
  ACTIVE: 'bg-green-100 text-green-700',
  RETURNED: 'bg-silver-100 text-silver-600',
  REJECTED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-silver-100 text-silver-500',
};

export function MyRentals() {
  const { user, openAuthModal } = useAppStore();

  const { data: rentals, isLoading } = useQuery<Rental[]>({
    queryKey: ['myRentals'],
    queryFn: async () => {
      const { data } = await apiClient.get('/rentals/borrowing');
      return data.data;
    },
    enabled: !!user,
  });

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="text-5xl mb-3">📅</div>
        <p className="font-semibold text-silver-800 text-lg mb-2">My Rentals</p>
        <p className="text-silver-500 mb-6">Sign in to see your rental history.</p>
        <button onClick={() => openAuthModal('login')} className="btn-primary">Sign In</button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10 text-center text-silver-400">
        <div className="text-4xl mb-3 animate-bounce-gentle">📅</div>
        <p>Loading your rentals…</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className="font-display font-extrabold text-2xl text-silver-800 mb-6">My Rentals</h1>

      {!rentals?.length ? (
        <div className="text-center py-16 text-silver-400">
          <div className="text-5xl mb-3">📦</div>
          <p className="font-medium mb-2">No rentals yet</p>
          <p className="text-sm mb-6">Find tools from your neighbors and start renting!</p>
          <Link to="/browse" className="btn-primary inline-block">Browse Tools</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {rentals.map((rental) => (
            <Link key={rental.id} to={`/rentals/${rental.id}`}
              className="card flex items-center gap-4 p-4 hover:border hover:border-silver-200">
              {/* Tool image */}
              <div className="w-16 h-16 rounded-2xl bg-silver-100 overflow-hidden flex-shrink-0">
                {rental.tool?.images?.[0]?.url ? (
                  <img src={rental.tool.images[0].url} alt={rental.tool.name}
                    className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xl">🔧</div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-silver-800 truncate">{rental.tool?.name}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-silver-400">
                  <span className="flex items-center gap-1">
                    <CalendarDays size={12} />
                    {new Date(rental.startDate).toLocaleDateString()} –{' '}
                    {new Date(rental.endDate).toLocaleDateString()}
                  </span>
                  {rental.tool?.city && (
                    <span className="flex items-center gap-1">
                      <MapPin size={12} /> {rental.tool.city}
                    </span>
                  )}
                </div>
                <div className="mt-2">
                  <span className={`badge text-xs ${statusColors[rental.status] ?? 'bg-silver-100 text-silver-600'}`}>
                    {RENTAL_STATUS_LABELS[rental.status] ?? rental.status}
                  </span>
                </div>
              </div>

              <div className="text-right flex-shrink-0">
                <p className="font-bold text-silver-800">${Number(rental.totalAmount).toFixed(2)}</p>
                <ChevronRight size={16} className="text-silver-300 ml-auto mt-1" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
